import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import type { To } from 'react-router';
import { useCallback, useState } from 'react';

import type { SpacingScale } from '../../utilities/shared/sizes';
import { selectors } from '../../common/selectors';
import { shadows } from '../../common/shadows';
import { sizing } from '../../common/sizing';
import { getVariantStyles, variantBodyColor } from '../../common/variant';
import { visuallyHiddenCSS } from '../../common/visually_hidden';
import { stateZIndexes } from '../../common/z_indexes';
import { Badge } from '../../components/Badge/Badge';
import { type BadgeGroupItemProps, BadgeGroup } from '../../components/Badge/BadgeGroup';
import { EmptyState } from '../../components/EmptyState/EmptyState';
import { type StatProps, Stats } from '../../components/Stats/Stats';
import { Tooltip } from '../../components/Tooltip/Tooltip';
import { ControlGroup } from '../../controls/ControlGroup/ControlGroup';
import { UtilityControl } from '../../controls/UtilityControl/UtilityControl';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { Text } from '../../text/Text';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { type GutterProp, gutterCSS } from '../../utilities/shared/Gutter';
import { HStack } from '../../utilities/Stack/HStack';

const taskVars = {
  padding: {
    ends: 8,
    sides: 12,
  },
} satisfies {
  padding: {
    ends: SpacingScale;
    sides: SpacingScale;
  };
};

export type TaskCollapseProps = {
  /**
   * When set, collapsed state is controlled by the parent.
   */
  collapsed?: boolean;
  /**
   * Initial collapsed state when `collapsed` is omitted (uncontrolled). Defaults to `false`.
   */
  defaultCollapsed?: boolean;
  /**
   * Called whenever the user toggles expand/collapse.
   */
  onCollapsedChange?: (collapsed: boolean) => void;
  /**
   * The label of the control.
   */
  label?: {
    expanded?: string;
    collapsed?: string;
  };
};

type TaskCollapseControlProps = Pick<TaskCollapseProps, 'collapsed' | 'label'> & {
  /**
   * The function to toggle the collapsed state.
   */
  onToggle: () => void;
  /**
   * The role of the task.
   */
  role?: TaskProps['role'];
};

function TaskCollapse({ collapsed, label, onToggle, role }: TaskCollapseControlProps) {
  const effectiveLabelCollapsed = label?.collapsed ?? 'Expand';
  const effectiveLabelExpanded = label?.expanded ?? 'Collapse';
  return (
    <UtilityControl
      icon={collapsed ? 'chevron-right' : 'chevron-down'}
      label={collapsed ? effectiveLabelCollapsed : effectiveLabelExpanded}
      onClick={onToggle}
      variant={role === 'child' ? 'application' : 'neutral'}
    />
  );
}

type TaskStateProps = {
  /**
   * The state of the task.
   */
  state: 'read' | 'unread';
};

const TaskStateLabel = styled(Text, {
  ...visuallyHiddenCSS,
});
TaskStateLabel.displayName = 'TaskStateLabel';

const TaskStateContainer = styled('div', {
  size: '$6',
  background: colors.iconBrandLight,
  borderRadius: '100%',

  [darkThemeSelector]: {
    background: colors.iconBrandDark,
  },

  variants: {
    state: {
      read: {},
      unread: {
        display: 'flex',
      },
    },
  },
});
TaskStateContainer.displayName = 'TaskStateContainer';

function TaskState({ state }: TaskStateProps) {
  if (state === 'read') return null;

  return (
    <TaskStateContainer state={state}>
      <TaskStateLabel>{state}</TaskStateLabel>
    </TaskStateContainer>
  );
}

export type TaskProps = {
  /**
   * The actions of the task.
   */
  actions?: ReactNode;
  /**
   * Task annotations as {@link Badge} props (rendered inside a {@link BadgeGroup}).
   */
  annotation?: BadgeGroupItemProps[];
  /**
   * The collapse action of the task.
   */
  collapse?: TaskCollapseProps;
  /**
   * The id of the task.
   */
  id: string;
  /**
   * The label of the task.
   */
  label: ReactNode;
  /**
   * The role of the task, this is set by the component.
   */
  role?: 'parent' | 'child';
  /**
   * Summary stats shown above subtasks inside the same collapsible region as subtasks when
   * {@link collapse} is set (stats hide and show with expand/collapse).
   */
  stats?: StatProps[];
  /**
   * Highlights this row (e.g. current task open in a split/detail pane).
   */
  selected?: boolean;
  /**
   * Set by {@link Tasks}: true when any top-level task has at least one subtask. Used so
   * expand/collapse still appears for tasks with no direct subtasks when siblings do.
   */
  siblingTasks?: boolean;
  /**
   * Subtasks to render under this task. Omit to hide the subtasks region entirely; pass an
   * empty array to show {@link EmptyState}; pass one or more tasks to render them. When
   * {@link collapse} is set, the control shows if this task has subtasks, or if this task has
   * an explicit `subtasks` value (including `[]`) while {@link Tasks} sets `siblingTasks` because
   * another top-level task has subtasks—so the column stays aligned.
   */
  subtasks?: TaskProps[];
  // Routing / interaction
  as?: ElementType;
  to?: To;
  onClick?: ComponentPropsWithoutRef<'button'>['onClick'];
} & TaskStateProps;

const TaskLabel = styled(Text, {
  color: variantBodyColor,
  borderRadius: '$6',

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    isPressable: {
      true: {
        cursor: 'pointer',
        ...getVariantStyles('brand'),

        [selectors.focus]: {
          boxShadow: shadows.focusRingLight,

          [darkThemeSelector]: {
            boxShadow: shadows.focusRingDark,
          },
        },
      },
      false: {
        ...getVariantStyles('neutral'),
      },
    },
  },
});
TaskLabel.displayName = 'TaskLabel';

const TaskActions = styled(ControlGroup);
TaskActions.displayName = 'TaskActions';

const TaskBody = styled(Text, {
  display: 'flex',
  flexDirection: 'row',
});
TaskBody.displayName = 'TaskBody';

const TaskCollapsible = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  margin: '$8 0',
  background: colors.bgSurfaceLight,
  strokeAll: colors.strokeNeutralLight,
  borderRadius: '$10',

  [darkThemeSelector]: {
    background: colors.bgSurfaceDark,
    strokeAll: colors.strokeNeutralDark,
  },

  variants: {
    collapsed: {
      true: {
        display: 'none',
      },
      false: {},
    },
  },
});
TaskCollapsible.displayName = 'TaskCollapsible';

const TaskStats = styled('div', {
  padding: '$8 $16',

  '&:not(:last-child)': {
    strokeBottom: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      strokeBottom: colors.strokeNeutralDark,
    },
  },
});
TaskStats.displayName = 'TaskStats';

const TaskSubtasks = styled('div', {
  background: colors.bgNeutralLight,
  strokeAll: colors.strokeNeutralLight,

  [darkThemeSelector]: {
    background: colors.bgNeutralDark,
    strokeAll: colors.strokeNeutralDark,
  },

  '&:not(:first-child)': {
    borderRadius: '0 0 $10 $10',
  },
});
TaskSubtasks.displayName = 'TaskSubtasks';

const TaskContent = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  widthAll: '100%',
});
TaskContent.displayName = 'TaskContent';

const TaskContainer = styled('div', {
  position: 'relative',
  zIndex: stateZIndexes.base,
  display: 'flex',
  flexDirection: 'column',
  padding: `$${taskVars.padding.ends} $${taskVars.padding.sides}`,

  '&::before, &::after': {
    content: '',
    position: 'absolute',
    right: `calc(${sizing.sides} - (${sizing.sides} - $${taskVars.padding.sides}))`,
    left: `calc(${sizing.sides} - (${sizing.sides} - $${taskVars.padding.sides}))`,
    display: 'none',
    height: '$1',
    background: colors.strokeNeutralLight,

    [darkThemeSelector]: {
      background: colors.strokeNeutralDark,
    },
  },

  '&::before': {
    top: '-0.5px',
  },

  '&::after': {
    bottom: '-0.5px',
  },

  [selectors.hover]: {
    zIndex: stateZIndexes.hover,
  },

  [selectors.focus]: {
    zIndex: stateZIndexes.focus,
  },

  variants: {
    role: {
      child: {
        background: colors.bgNeutralLight,
        strokeAll: colors.strokeNeutralLight,

        [darkThemeSelector]: {
          background: colors.bgNeutralDark,
          strokeAll: colors.strokeNeutralDark,
        },

        '&:last-child': {
          borderRadiusBottom: '$10',
        },
      },
      parent: {
        background: colors.bgApplicationLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
        },
      },
    },
    selected: {
      true: {
        zIndex: stateZIndexes.focus,
        backgroundColor: colors.bgBrandLight,
        strokeAll: colors.strokeBrandLight,
        borderRadius: '$10',

        [darkThemeSelector]: {
          backgroundColor: colors.bgBrandDark,
          strokeAll: colors.strokeBrandDark,
        },
      },
      false: {},
    },
  },

  compoundVariants: [
    {
      selected: false,
      role: 'parent',
      css: {
        '&:not(:first-child)::before': {
          display: 'flex',
        },

        '&:not(:last-child)::after': {
          display: 'flex',
        },
      },
    },
  ],
});
TaskContainer.displayName = 'TaskContainer';

const TasksContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',

  variants: {
    isSubtasksContainer: {
      false: {},
      true: {},
    },
  },
  defaultVariants: {
    isSubtasksContainer: false,
  },
});
TasksContainer.displayName = 'TasksContainer';

export function Task({
  actions,
  annotation,
  collapse,
  id,
  label,
  role = 'parent',
  selected = false,
  siblingTasks,
  state,
  stats,
  subtasks,
  // Routing / interaction
  as,
  to,
  onClick,
}: TaskProps) {
  const isPressable = Boolean(onClick || (as && to));
  const hasSubtasks = (subtasks?.length ?? 0) > 0;
  /**
   * Two empty checks because;
   * - `subtasks` was passed but has no items (such as `[]`), so we show an empty state.
   * - `subtasks` wasn't passed, so we don't show subtasks region at all.
   */
  const hasSubtasksExplicitly = subtasks !== undefined;
  const hasSubtasksButEmpty = hasSubtasksExplicitly && !hasSubtasks;
  const hasStats = (stats?.length ?? 0) > 0;

  const showCollapse = Boolean(
    collapse && (hasSubtasks || (siblingTasks && hasSubtasksExplicitly) || hasStats),
  );

  /** Subtasks region and/or stats: all share the same expand/collapse visibility. */
  const showCollapsibleRegion = hasSubtasks || hasSubtasksButEmpty || hasStats;

  const [uncontrolledCollapsed, setUncontrolledCollapsed] = useState(
    () => collapse?.defaultCollapsed ?? false,
  );
  const isCollapsedControlled = collapse?.collapsed !== undefined;
  const collapsedFromProps = collapse?.collapsed;
  const isCollapsed =
    showCollapse && (isCollapsedControlled ? Boolean(collapsedFromProps) : uncontrolledCollapsed);

  const toggleCollapsed = useCallback(() => {
    if (!showCollapse || !collapse) return;
    const next = !isCollapsed;
    if (!isCollapsedControlled) {
      setUncontrolledCollapsed(next);
    }
    collapse.onCollapsedChange?.(next);
  }, [showCollapse, collapse, isCollapsed, isCollapsedControlled]);

  let subtasksSection: ReactNode = null;
  if (hasSubtasks) {
    subtasksSection = (
      <TaskSubtasks>
        <TasksContainer isSubtasksContainer>
          {subtasks!.map((subtask) => (
            <Task key={subtask.id} {...subtask} role="child" siblingTasks={siblingTasks} />
          ))}
        </TasksContainer>
      </TaskSubtasks>
    );
  } else if (hasSubtasksButEmpty) {
    subtasksSection = (
      <TaskSubtasks>
        <TasksContainer isSubtasksContainer>
          <EmptyState size="small" heading="No subtasks" />
        </TasksContainer>
      </TaskSubtasks>
    );
  }

  return (
    <TaskContainer id={id} role={role} selected={selected}>
      <AlignStack
        gap={12}
        preset="body"
        start={
          showCollapse && collapse ? (
            <TaskCollapse
              role={role}
              collapsed={isCollapsed}
              onToggle={toggleCollapsed}
              label={collapse.label}
            />
          ) : undefined
        }
      >
        <TaskContent>
          <TaskBody>
            <AlignStack
              gap={8}
              preset="body"
              start={
                state !== 'read' && (
                  <Tooltip contents="Unread">
                    <TaskState state={state} />
                  </Tooltip>
                )
              }
              end={
                <HStack align="center" spacing={8}>
                  {annotation != null && annotation.length > 0 && (
                    <BadgeGroup ends="pill" size="small">
                      {annotation.map((item) => (
                        <Badge key={item.id ?? String(item.label)} {...item} />
                      ))}
                    </BadgeGroup>
                  )}
                  {actions && (
                    <TaskActions
                      appearance="muted"
                      ghost
                      relation="joined"
                      size="x-small"
                      variant="secondary"
                    >
                      {actions}
                    </TaskActions>
                  )}
                </HStack>
              }
            >
              <TaskLabel
                role={isPressable ? 'button' : undefined}
                tabIndex={isPressable ? 0 : undefined}
                as={as}
                to={to}
                onClick={onClick}
                isPressable={isPressable}
                truncate
                weight="bold"
              >
                {label}
              </TaskLabel>
            </AlignStack>
          </TaskBody>
        </TaskContent>
      </AlignStack>
      {showCollapsibleRegion && (
        <TaskCollapsible collapsed={Boolean(showCollapse && isCollapsed)}>
          {hasStats && stats && (
            <TaskStats>
              <Stats
                gap={12}
                gutter={{ edge: 'none' }}
                proportions="auto"
                size="medium"
                stats={stats}
              />
            </TaskStats>
          )}
          {subtasksSection}
        </TaskCollapsible>
      )}
    </TaskContainer>
  );
}

type TasksProps = {
  tasks: TaskProps[];
  /**
   * When true, styles this container as the nested subtask list wrapper (also set automatically
   * on the inner {@link TasksContainer} inside each {@link Task} subtasks region).
   */
  isSubtasksContainer?: boolean;
} & GutterProp;

export function Tasks({
  gutter = { context: 'application', edge: 'all' },
  isSubtasksContainer = false,
  tasks,
}: TasksProps) {
  const siblingSubtasks = tasks.some((t) => (t.subtasks?.length ?? 0) > 0);
  const css = gutterCSS({
    ...(isSubtasksContainer ? { context: 'content', edge: 'none' } : gutter),
    adjustment: { ends: taskVars.padding.ends, sides: taskVars.padding.sides },
  });

  return (
    <TasksContainer css={css} isSubtasksContainer={isSubtasksContainer}>
      {tasks.map((task) => (
        <Task key={task.id} {...task} role="parent" siblingTasks={siblingSubtasks} />
      ))}
    </TasksContainer>
  );
}
